import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import QrScanner from 'react-qr-scanner';
import { validateStoryQRCode } from "../utils/api";
import logoImage from "../assets/images/ana-intercontinental-logo.png";
import beachBackground from "../assets/images/beach-background.png";
import animeCharacter from "../assets/images/anime-character.png";

// This is a placeholder for a better way to create a QR code
const QRCodePlaceholder = ({ onClick }) => (
  <div 
    className="bg-white border-2 border-black p-1 w-[150px] h-[150px] relative flex items-center justify-center cursor-pointer"
    onClick={onClick}
  >
    <div className="absolute inset-0 grid grid-cols-6 grid-rows-6">
      {Array(36).fill(0).map((_, i) => {
        // Create a pattern for QR code - just for visualization
        const isBlack = Math.random() > 0.7;
        // Make sure corners have the QR code patterns
        const isCorner = 
          (i < 8 && i % 6 < 2) || // Top left
          (i < 8 && i % 6 > 3) || // Top right
          (i > 27 && i % 6 < 2);  // Bottom left
        
        return (
          <div 
            key={i} 
            className={`${isCorner || isBlack ? 'bg-black' : 'bg-white'}`}
          />
        );
      })}
    </div>
    <div className="z-10 bg-white px-2 py-1 text-sm font-bold text-gray-500">SAMPLE</div>
  </div>
);

// Placeholder for prize image
const PrizeImage = ({ type }) => {
  const bgColor = type === 'beef' ? 'bg-red-100' : 
                  type === 'voucher' ? 'bg-blue-100' :
                  type === 'shisa' ? 'bg-yellow-100' :
                  type === 'soba' ? 'bg-green-100' :
                  type === 'beer' ? 'bg-amber-100' : 'bg-gray-100';
  
  return (
    <div className={`w-12 h-12 ${bgColor} rounded flex items-center justify-center`}>
      {type === 'beef' && <span className="text-[8px]">🥩</span>}
      {type === 'voucher' && <span className="text-[8px]">🎫</span>}
      {type === 'shisa' && <span className="text-[8px]">🦁</span>}
      {type === 'soba' && <span className="text-[8px]">🍜</span>}
      {type === 'beer' && <span className="text-[8px]">🍺</span>}
      {type === 'sticker' && <span className="text-[8px]">🏷️</span>}
    </div>
  );
};

// Game machine
const GameMachine = () => (
  <div className="bg-yellow-300/70 w-16 h-16 rounded-md flex flex-col items-center justify-center border border-yellow-500">
    <div className="bg-white h-8 w-10 rounded-t-md flex items-center justify-center">
      <div className="flex space-x-1">
        <div className="w-1 h-2 bg-red-400 rounded-full"></div>
        <div className="w-1 h-2 bg-blue-400 rounded-full"></div>
        <div className="w-1 h-2 bg-green-400 rounded-full"></div>
      </div>
    </div>
    <div className="bg-gray-200 h-4 w-10"></div>
    <div className="bg-amber-600 h-2 w-4 mt-0.5 rounded-sm"></div>
  </div>
);

const WelcomePage = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [scanStatus, setScanStatus] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Clear localStorage when the component mounts
  useEffect(() => {
    console.log("Clearing localStorage on WelcomePage load");
    // Clear all game-related data
    localStorage.removeItem('userId');
    localStorage.removeItem('roomNumber');
    localStorage.removeItem('guestName');
    localStorage.removeItem('storyId');
    localStorage.removeItem('hotelId');
    localStorage.removeItem('hotelName');
    // Any other game-related data that might be stored should be cleared here
  }, []);

  const handleScan = async (data) => {
    if (data && data.text) {
      console.log('QR Code scanned:', data.text);
      setResult(data);
      setIsProcessing(true);
      setScanStatus('QRコードを読み取りました。処理中...');
      
      try {
        // Send QR code data to backend for validation
        const response = await validateStoryQRCode(data);
        console.log('Validation response:', response);
        
        if (response.success && response.data) {
          // Store story ID in localStorage
          const storyId = response.data.storyId;
          localStorage.setItem('storyId', storyId);
          
          // Show success message
          setScanStatus('ストーリーのQRコードを確認しました。ゲームを開始します...');
          
          // Wait a moment for user to see the success message
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Navigate directly to the story page with the storyId
          navigate(`/story/${storyId}`);
        } else {
          console.log('Invalid QR code response:', response);
          setScanStatus('無効なQRコードです。ストーリーのQRコードをスキャンしてください。');
          setTimeout(() => {
            setScanning(false);
            setIsProcessing(false);
            setScanStatus('');
          }, 3000);
        }
      } catch (error) {
        console.error('Error validating QR code:', error);
        
        // Show more specific error message based on the error
        let errorMessage = 'エラーが発生しました。もう一度お試しください。';
        
        if (error && error.message) {
          if (error.message.includes('Story not found')) {
            errorMessage = 'ストーリーが見つかりませんでした。正しいQRコードをスキャンしてください。';
          } else if (error.message.includes('Invalid QR code')) {
            errorMessage = '無効なQRコード形式です。正しいQRコードをスキャンしてください。';
          } else if (error.message.includes('Network Error')) {
            errorMessage = 'ネットワークエラーが発生しました。接続を確認してください。';
          }
        }
        
        setScanStatus(errorMessage);
        
        setTimeout(() => {
          setScanning(false);
          setIsProcessing(false);
          setScanStatus('');
        }, 3000);
      }
    }
  };

  const handleError = (err) => {
    console.error('QR Scanner error:', err);
    setScanStatus('QRコードスキャン中にエラーが発生しました。もう一度お試しください。');
    setTimeout(() => {
      setScanning(false);
      setIsProcessing(false);
      setScanStatus('');
    }, 2000);
  };

  const toggleScanner = () => {
    if (!scanning) {
      setScanStatus('');
      setIsProcessing(false);
    }
    setScanning(!scanning);
  };

  // Navigate to home page
  const goToHome = () => {
    navigate('/home');
  };

  return (
    <div 
      className="relative bg-cover bg-center bg-no-repeat max-w-[480px] mx-auto min-h-svh p-4 flex flex-col items-center overflow-hidden" 
      style={{ 
        backgroundImage: `url(${beachBackground})`, 
        fontFamily: 'Hiragino'
      }}
    >
      {/* Logo */}
      <div className="flex flex-col text-center mb-1 mt-6">
        <img
          src={logoImage}
          alt="ANA Intercontinental"
          className="h-[40px] w-auto object-contain mx-auto"
        />
        <h1 className="text-[#1B3D6E] text-md font-serif -tracking-wide">
          ANA
          <br />
          INTERCONTINENTAL®
        </h1>
        <p className="text-[#1B3D6E] text-sm -tracking-normal">
          MANZA BEACH RESORT
        </p>
      </div>

      {/* Main Content */}
      <div className="rounded-lg p-4 w-full max-w-[440px] mx-auto space-y-4 mb-8">
        {/* Title */}
        <div className="text-center w-full text-black">
          <h2 className="text-xl mb-2 font-normal">
            観光アンバサダー比嘉さんと
          </h2>
          <h1 className="text-4xl leading-none mb-2 font-medium tracking-widest">
            青い海の秘密
          </h1>
          <p className="text-md font-normal mb-3">
            〜沖縄の妖精きじむなーからの挑戦状〜
          </p>
        </div>

        {/* Game Instructions */}
        <div className="text-center text-lg font-bold py-1 rounded">
          【ゲームをプレイする場合】
        </div>
        <div className="text-center text-sm">
          下記のQRコードをお手持ちのスマートフォンで読み込んで下さい。<br />
          ストーリーのQRコードをスキャンしてゲームを開始します。
        </div>

        {/* QR Code Scanner */}
        <div className="flex justify-center relative">
          {scanning ? (
            <div className="relative w-[250px] h-[250px] bg-black rounded-lg overflow-hidden">
              <QrScanner
                delay={300}
                onError={handleError}
                onScan={handleScan}
                style={{ width: '100%' }}
                constraints={{
                  video: { facingMode: 'environment' }
                }}
              />
              
              {/* Status message */}
              {scanStatus && (
                <div className="absolute top-0 left-0 right-0 bg-black/70 text-white p-2 text-center text-sm">
                  {scanStatus}
                </div>
              )}
              
              {/* Cancel button (disabled during processing) */}
              <button 
                onClick={toggleScanner}
                disabled={isProcessing}
                className={`absolute bottom-3 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded text-sm z-10 
                  ${isProcessing ? 'bg-gray-400 cursor-not-allowed' : 'bg-white cursor-pointer'}`}
              >
                {isProcessing ? '処理中...' : 'キャンセル'}
              </button>
            </div>
          ) : (
            <QRCodePlaceholder onClick={toggleScanner} />
          )}
        </div>

        {/* Scan Instructions */}
        {!scanning && (
          <div className="text-center text-sm font-medium text-blue-600">
            QRコードをタップするとスキャナーが開きます
          </div>
        )}

        {/* Anime Character */}
        <div className="absolute right-1 bottom-[430px]">
          <img src={animeCharacter} className="w-[140px]" alt="Character" />
        </div>

        {/* Prizes Section */}
        <div className="bg-white rounded-lg p-3 shadow-md mt-4">
          <div className="text-center mb-2 py-1 rounded font-bold">
            【ゲームクリア後の景品一覧】
          </div>
          <div className="text-center text-sm mb-2">
            *ホテルの名前*限定商品
          </div>
          <div className="text-sm">
            ゲームをゴールした方には下記の景品が当たるガチャを回す事ができます。
          </div>

          {/* Prize List */}
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            <div className="flex items-start space-x-1">
              <PrizeImage type="beef" />
              <div>
                <div className="text-red-600 font-bold">**特賞**</div>
                <div>石垣牛400g<br/>サーロインステーキ<br/>5名様</div>
              </div>
            </div>
            <div className="flex items-start space-x-1">
              <PrizeImage type="voucher" />
              <div>
                <div className="text-red-600 font-bold">**1等**</div>
                <div>ホテル商品券<br/>売店で使用可能<br/>5名様</div>
              </div>
            </div>
            <div className="flex items-start space-x-1">
              <PrizeImage type="shisa" />
              <div>
                <div className="text-red-600 font-bold">**2等**</div>
                <div>限定シーサー<br/>XXXX工房さま<br/>5名様</div>
              </div>
            </div>
            <div className="flex items-start space-x-1">
              <PrizeImage type="soba" />
              <div>
                <div className="text-red-600 font-bold">**3等**</div>
                <div>国際通りで使える<br/>沖縄そば2人前<br/>5名様</div>
              </div>
            </div>
            <div className="flex items-start space-x-1">
              <PrizeImage type="beer" />
              <div>
                <div className="text-red-600 font-bold">**4等**</div>
                <div>オリオンビール<br/>1ケース<br/>5名様</div>
              </div>
            </div>
            <div className="flex items-start space-x-1">
              <PrizeImage type="sticker" />
              <div>
                <div className="text-red-600 font-bold">**5等**</div>
                <div>きじむなー<br/>オリジナルステッカー<br/>5名様</div>
              </div>
            </div>
          </div>
        </div>

        {/* Limited Time */}
        <div className="absolute right-1 bottom-24 bg-yellow-200 p-2 rounded-lg w-[150px] text-center text-sm transform rotate-3">
          <div className="font-bold">期間限定商品！</div>
          <div className="text-xs">最高の思い出を作ろう！</div>
          <div className="mt-2 flex items-center justify-center">
            <GameMachine />
          </div>
        </div>
      </div>

    </div>
  );
};

export default WelcomePage; 